import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { QuantAnalysisFilterContext } from '@trinity/chat-components';
import { AppThemingContext, type AppThemingContextValue } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { X as CloseIcon, Plus as PlusIcon } from 'react-feather';
import type { CSSObjectWithLabel } from 'react-select';
import Select from 'react-select';

type Props = {
  columnNames: string[];
  rowNames: string[];
  selectOptions: Array<{ value: string; label: string }>;
  analysisName: string;
};

export const AnalysisFilterMenu = ({
  columnNames,
  rowNames,
  selectOptions,
  analysisName,
}: Props) => {
  const [quantFilter, setQuantFilter] = useContext(QuantAnalysisFilterContext);
  const appThemingContext = useContext(AppThemingContext);

  // Dynamic color for close icon
  const iconColor = appThemingContext?.enabled
    ? 'var(--gray-d)'
    : '#222';

  // Compose filterAttributes from quantFilter context
  const filterAttributes = useMemo(() => {
    const attrs: { id: number; value: string }[] = [];

    quantFilter.columnNames.forEach((col, idx) => {
      attrs.push({ id: quantFilter.rowNames.length + idx, value: col });
    });

    quantFilter.rowNames.forEach((row, idx) => {
      attrs.push({ id: idx, value: row });
    });

    if (attrs.length === 0) {
      attrs.push({ id: 0, value: '' });
    }
    return attrs;
  }, [quantFilter.rowNames, quantFilter.columnNames]);

  const addFilterAttribute = () => {
    const allAttrs = [...filterAttributes, { id: filterAttributes.length, value: '' }];

    const validRowNames = allAttrs.filter(r => rowNames.includes(r.value)).map(r => r.value);
    const validColNames = allAttrs.filter(r => columnNames.includes(r.value)).map(r => r.value);
    const hasBlank = allAttrs.some(r => r.value === '');
    setQuantFilter((current) => ({
      ...current,
      columnNames: validColNames,
      rowNames: hasBlank ? [...validRowNames, ''] : validRowNames,
    }));
  };

  const removeFilterAttribute = (id: number) => {
    const allAttrs = filterAttributes.filter(r => r.id !== id);
    setQuantFilter((current) => ({
      ...current,
      columnNames: allAttrs.filter(r => columnNames.includes(r.value)).map(r => r.value),
      rowNames: allAttrs.filter(r => rowNames.includes(r.value)).map(r => r.value),
    }));
  };

  const updateFilterAttribute = (id: number, value: string) => {
    const allAttrs = filterAttributes.map(r => r.id === id ? { ...r, value } : r);
    setQuantFilter((current) => ({
      ...current,
      columnNames: allAttrs.filter(r => columnNames.includes(r.value)).map(r => r.value),
      rowNames: allAttrs.filter(r => rowNames.includes(r.value)).map(r => r.value),
    }));
  };

  const getAvailableOptions = useCallback(() => {
    const selectedValues = filterAttributes
      .filter(attr => attr.value !== '')
      .map(attr => attr.value);

    return selectOptions.filter(option => !selectedValues.includes(option.value));
  }, [filterAttributes, selectOptions]);

  const getSelectStyles = useCallback(() => {
    const primaryColor = appThemingContext?.enabled
      ? (appThemingContext.palette?.primary?.main || appThemingContext.theme?.color?.primary)
      : undefined;

    return {
      control: provided => ({
        ...provided,
        minHeight: 32,
        height: 'auto',
        borderRadius: 5,
        borderColor: 'var(--gray-l)',
        boxShadow: 'none',
        fontSize: 14,
        '&:hover': {
          borderColor: 'var(--gray-m)',
        },
        '&:focus-within': {
          borderColor: 'var(--gray-m)',
          outline: 'none',
        },
      }),
      indicatorsContainer: provided => ({
        ...provided,
        height: 32,
      }),
      valueContainer: provided => ({
        ...provided,
        minHeight: 32,
        height: 'auto',
        padding: '6px 8px',
      }),
      input: provided => ({
        ...provided,
        margin: 0,
        padding: 0,
      }),
      dropdownIndicator: provided => ({
        ...provided,
      }),
      clearIndicator: provided => ({
        ...provided,
        padding: 4,
      }),
      menu: provided => ({
        ...provided,
        zIndex: 9999,
        borderRadius: 5,
        border: '1px solid var(--gray-l)',
      }),
      singleValue: provided => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
      }),
      option: (provided: CSSObjectWithLabel, state: { isSelected: boolean; isFocused: boolean }): CSSObjectWithLabel => ({
        ...provided,
        backgroundColor: state.isSelected
          ? (primaryColor ? `${primaryColor}20` : 'transparent')
          : state.isFocused
            ? (primaryColor ? `${primaryColor}10` : 'var(--gray-xl)')
            : 'transparent',
        color: 'inherit',
        '&:hover': {
          backgroundColor: primaryColor ? `${primaryColor}10` : 'var(--gray-xl)',
        },
      } as CSSObjectWithLabel),
      multiValue: provided => ({
        ...provided,
        display: 'none !important',
        backgroundColor: 'transparent',
      }),
      multiValueLabel: provided => ({
        ...provided,
        display: 'none !important',
      }),
      multiValueRemove: provided => ({
        ...provided,
        display: 'none !important',
      }),
    };
  }, [appThemingContext]);

  return (
    <FilterMenu>
      <SectionTitle>Filters</SectionTitle>
      <AnalysisTabSection>
        Analyses Tab: <AnalysisTabValue>{analysisName}</AnalysisTabValue>
      </AnalysisTabSection>
      {filterAttributes.map(row => (
        <FilterRow key={row.id}>
          <SelectContainer>
            <Select
              value={row.value ? { value: row.value, label: row.value } : null}
              options={getAvailableOptions()}
              onChange={opt => updateFilterAttribute(row.id, opt?.value || '')}
              placeholder="Select a value"
              styles={{
                ...getSelectStyles(),
                control: provided => ({
                  ...getSelectStyles().control(provided),
                  width: '100%',
                }),
              }} />
          </SelectContainer>
          <StyleButton onClick={() => removeFilterAttribute(row.id)}>
            <CloseIcon size={18} color={iconColor} />
          </StyleButton>
        </FilterRow>
      ))}
      <AddFilterRow onClick={addFilterAttribute}>
        <AddFilterText appThemingContext={appThemingContext}>
          <PlusIcon size={16} />
          Add filter
        </AddFilterText>
      </AddFilterRow>
    </FilterMenu>
  );
};

const StyleButton = styled.button({
  background: 'none',
  border: 'none',
  padding: 6,
  marginLeft: 8,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '18px',
  color: 'var(--gray-d)',
  '&:hover': {
    backgroundColor: 'var(--gray-xl)',
  },
});

const FilterMenu = styled.div({
  padding: '16px',
  background: '#fff',
  borderRadius: '5px',
  border: '1px solid var(--gray-l)',
  minWidth: 420,
  maxWidth: 500,
});

const SectionTitle = styled.div({
  fontWeight: 600,
  marginBottom: 8,
});

const AnalysisTabSection = styled.div({
  padding: `0 0 12px 0`,
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--black-d)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const AnalysisTabValue = styled.span({
  fontWeight: 400,
  color: 'var(--gray-d)',
  marginLeft: 4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 200,
  display: 'inline-block',
});

const FilterRow = styled.div({
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  marginBottom: 4,
});

const SelectContainer = styled.div({
  flex: 1,
  '& .react-select__multi-value': {
    display: 'none !important',
    backgroundColor: 'transparent !important',
  },
  '& .react-select__multi-value__label': {
    display: 'none !important',
  },
  '& .react-select__multi-value__remove': {
    display: 'none !important',
  },
  '& .react-select__single-value': {
    backgroundColor: 'transparent !important',
    color: 'inherit !important',
  },
});

const AddFilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginTop: 8,
  cursor: 'pointer',
});

const AddFilterText = styled.span<{ appThemingContext: AppThemingContextValue | null }>(({ appThemingContext }) => {
  const primaryColor = appThemingContext?.enabled
    ? (appThemingContext.palette?.primary?.main || appThemingContext.theme?.color?.primary)
    : undefined;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: primaryColor || 'var(--black)',
    fontFamily: 'var(--font-semibold)',
    fontSize: 15,
    userSelect: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid var(--gray-m)',
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: primaryColor ? `${primaryColor}20` : 'var(--gray-xl)',
    },
  };
});
